/**
 * ArrayListWidget - Custom widget for multi-selection with a vertical list.
 * Allows adding items one by one, displaying them in a list, each with a remove button.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getVocabulary } from '@plone/volto/actions/vocabularies/vocabularies';
import FormFieldWrapper from '@plone/volto/components/manage/Widgets/FormFieldWrapper';
import {
  ClearIndicator,
  customSelectStyles,
  DropdownIndicator,
  MenuList,
  MultiValueContainer,
  Option,
  selectTheme,
  SortableMultiValue,
  SortableMultiValueLabel,
} from '@plone/volto/components/manage/Widgets/SelectStyling';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import { injectLazyLibs } from '@plone/volto/helpers/Loadable/Loadable';
import {
  getVocabFromField,
  getVocabFromHint,
  getVocabFromItems,
} from '@plone/volto/helpers/Vocabularies/Vocabularies';
import find from 'lodash/find';
import isObject from 'lodash/isObject';
import PropTypes from 'prop-types';
import { defineMessages, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Button, Input } from 'semantic-ui-react';

import addSVG from '@plone/volto/icons/add.svg';
import clearSVG from '@plone/volto/icons/clear.svg';

const messages = defineMessages({
  select: {
    id: 'Select…',
    defaultMessage: 'Select…',
  },
  no_value: {
    id: 'No value',
    defaultMessage: 'No value',
  },
  add: {
    id: 'Add new option',
    defaultMessage: 'Add new option',
  },
  add_custom: {
    id: 'Or create a new option…',
    defaultMessage: 'Or create a new option…',
  },
  remove: {
    id: 'Remove item',
    defaultMessage: 'Remove item',
  },
  cancel_add: {
    id: 'Cancel add',
    defaultMessage: 'Cancel add',
  },
});

/**
 * Compare if the input value already exists in the options (prevents duplicates)
 * @param {string} inputValue
 * @param {object} option
 * @param {object} accessors
 * @returns {boolean}
 */
function compareOption(inputValue = '', option, accessors) {
  const candidate = String(inputValue);
  const optionValue = String(accessors.getOptionValue(option));
  const optionLabel = String(accessors.getOptionLabel(option));
  return optionValue === candidate || optionLabel === candidate;
}

/**
 * Move an item from one index to another in an array
 * @param {Array} array
 * @param {number} from
 * @param {number} to
 * @returns {Array}
 */
function arrayMove(array, from, to) {
  const arr = array.slice();
  arr.splice(to < 0 ? arr.length + to : to, 0, arr.splice(from, 1)[0]);
  return arr;
}

/**
 * Normalize the received options to the format {value, label}
 * @param {Array} choices
 * @returns {Array}
 */
function normalizeChoices(choices) {
  if (Array.isArray(choices) && choices.length && Array.isArray(choices[0])) {
    return choices.map((option) => ({
      value: option[0],
      label: option[1] !== 'None' && option[1] ? option[1] : option[0],
    }));
  }
  return choices;
}

/**
 * Normalize the selected array value to the format [{label, value}]
 * @param {Array} choices
 * @param {Array} value
 * @returns {Array}
 */
function normalizeArrayValue(choices, value) {
  if (!value || !Array.isArray(value)) return [];
  if (value.length === 0) return value;
  if (typeof value[0] === 'string') {
    return value.map((v) => ({
      label: find(choices, (c) => c.value === v)?.label || v,
      value: v,
    }));
  }
  if (isObject(value[0]) && Object.keys(value[0]).includes('token')) {
    return value
      .map((v) => {
        const item = find(choices, (c) => c.value === v.token);
        return item
          ? {
              label: item.label || item.title || item.token,
              value: v.token,
            }
          : {
              label: v.label,
              value: v.token,
            };
      })
      .filter(Boolean);
  }
  return [];
}

/**
 * Multi-select widget with vertical list and the possibility to create new options.
 */
const ArrayListWidget = (props) => {
  const {
    id,
    value: propsValue = [],
    onChange,
    placeholder,
    intl,
    choices: propChoices = [],
    creatable: propCreatable,
    placeholderInput,
    disabled,
    isDisabled,
    ref,
    onBlur,
    fieldSet,
    minLength,
    maxLength,
    items,
    vocabBaseUrl,
    vocabLoading,
    vocabLoaded,
    lang,
    default: defaultValues,
    getVocabulary: getVocabularyProp,
  } = props;

  // Fetch the vocabulary on mount and when dependencies change (componentDidUpdate)
  useEffect(() => {
    if (
      !items?.choices?.length &&
      !propChoices?.length &&
      vocabLoading === undefined &&
      !vocabLoaded &&
      vocabBaseUrl
    ) {
      getVocabularyProp({
        vocabNameOrURL: vocabBaseUrl,
        size: -1,
        subrequest: lang,
      });
    }
  }, [
    items?.choices?.length,
    propChoices?.length,
    vocabBaseUrl,
    vocabLoading,
    vocabLoaded,
    lang,
    getVocabularyProp,
  ]);

  useEffect(() => {
    if (
      (propsValue === null || propsValue === undefined) &&
      defaultValues &&
      defaultValues.length > 0
    ) {
      onChange(id, [normalizeChoices(defaultValues)]);
    }
  }, [defaultValues, id, onChange, propsValue]);

  const choices = useMemo(
    () => normalizeChoices(propChoices || []),
    [propChoices],
  );
  const selectedOptions = useMemo(
    () => normalizeArrayValue(choices, propsValue),
    [choices, propsValue],
  );
  const formattedValue = useMemo(
    () => normalizeArrayValue(choices || [], propsValue || []),
    [choices, propsValue],
  );

  const [inputValue, setInputValue] = useState('');
  const [addingNew, setAddingNew] = useState(false);

  // Remove already selected options
  const availableChoices = useMemo(
    () =>
      choices.filter((c) => !selectedOptions.find((s) => s.value === c.value)),
    [choices, selectedOptions],
  );

  /**
   * Reorder the list items
   */
  const onSortEnd = useCallback(
    ({ oldIndex, newIndex }) => {
      const newSelected = arrayMove(selectedOptions, oldIndex, newIndex);
      onChange(
        id,
        newSelected.map((item) => item.value),
      );
    },
    [selectedOptions, onChange, id],
  );

  /**
   * Add a new option
   */
  const handleAdd = (option) => {
    if (!option) return;
    if (!option.value || selectedOptions.find((s) => s.value === option.value))
      return;

    const newValue = option
      ? [...(formattedValue || []), option].map((item) => item.value)
      : null;

    onChange(id, newValue);
    setAddingNew(false);
    setInputValue('');
  };

  /**
   * Remove an item from the list
   */
  const handleRemove = (val) => {
    const newValue = formattedValue
      ?.filter((item) => item.value !== val)
      ?.map((item) => item.value);

    onChange(id, newValue);
  };

  const Select = props.reactSelect.default;
  const { SortableContainer } = props.reactSortableHOC;
  const SortableSelect = SortableContainer(Select);

  const creatable =
    propChoices && !getVocabFromHint(props) && !propCreatable ? false : true;

  return (
    <FormFieldWrapper {...props}>
      <div
        className="w-add-item-button-wrapper"
        style={{ display: 'flex', marginBottom: 8, gap: 4 }}
      >
        {!addingNew && availableChoices.length > 0 && (
          <SortableSelect
            useDragHandle
            axis="xy"
            onSortEnd={(sortProp) => {
              onSortEnd(selectedOptions, sortProp);
            }}
            menuShouldScrollIntoView={false}
            distance={4}
            getHelperDimensions={({ node }) => node.getBoundingClientRect()}
            id={`field-${id}`}
            aria-labelledby={`fieldset-${fieldSet}-field-label-${id}`}
            key={id}
            isDisabled={disabled || isDisabled}
            className="react-select-container"
            classNamePrefix="react-select"
            options={availableChoices}
            styles={{
              ...customSelectStyles,
              container: (styles) => ({
                ...styles,
                flex: 1,
                marginRight: 8,
              }),
            }}
            theme={selectTheme}
            components={{
              ...(propChoices?.length > 25 && {
                MenuList,
              }),
              MultiValueContainer,
              MultiValue: SortableMultiValue,
              MultiValueLabel: SortableMultiValueLabel,
              DropdownIndicator,
              ClearIndicator,
              Option,
            }}
            placeholder={placeholder ?? intl.formatMessage(messages.select)}
            isValidNewOption={(
              inputValue,
              selectValue,
              selectOptions,
              accessors,
            ) =>
              !(
                !inputValue ||
                selectValue.some((option) =>
                  compareOption(inputValue, option, accessors),
                ) ||
                selectOptions.some((option) =>
                  compareOption(inputValue, option, accessors),
                )
              )
            }
            onChange={handleAdd}
          />
        )}
        {creatable && (availableChoices.length === 0 || addingNew) && (
          <>
            <Input
              id={`field-${id}`}
              name={id}
              value={inputValue}
              disabled={isDisabled}
              placeholder={placeholderInput ?? intl.formatMessage(messages.add)}
              onChange={({ target }) => setInputValue(target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  const val = inputValue.trim();
                  if (!val || selectedOptions.find((s) => s.value === val))
                    return;
                  handleAdd({ value: val, label: val });
                }
              }}
              ref={ref}
              onBlur={({ target }) => {
                if (onBlur) {
                  onBlur(id, target.value === '' ? undefined : target.value);
                }
              }}
              minLength={minLength}
              maxLength={maxLength}
              aria-label={placeholderInput ?? intl.formatMessage(messages.add)}
              style={{ flex: 1, marginRight: 8 }}
            />
            <Button.Group style={{ gap: 4 }}>
              <Button
                type="button"
                basic
                icon
                circular
                onClick={(e) => {
                  e.preventDefault();
                  const val = inputValue.trim();
                  if (!val || selectedOptions.find((s) => s.value === val))
                    return;
                  handleAdd({ value: val, label: val });
                }}
                disabled={
                  !inputValue.trim() ||
                  selectedOptions.find((s) => s.value === inputValue.trim())
                }
                aria-label={intl.formatMessage(messages.add)}
              >
                <Icon name={addSVG} size="18px" />
              </Button>
              {addingNew && (
                <Button
                  type="button"
                  basic
                  icon
                  circular
                  onClick={(e) => {
                    e.preventDefault();
                    setAddingNew(false);
                    setInputValue('');
                  }}
                  aria-label={intl.formatMessage(messages.cancel_add)}
                >
                  <Icon name={clearSVG} size="18px" />
                </Button>
              )}
            </Button.Group>
          </>
        )}
      </div>
      {creatable && !addingNew && availableChoices.length > 0 && (
        <Button
          compact
          icon
          aria-label={intl.formatMessage(messages.add_custom)}
          onClick={(e) => {
            e.preventDefault();
            setAddingNew(true);
          }}
        >
          <Icon name={addSVG} size="18px" />
          &nbsp;
          {intl.formatMessage(messages.add_custom)}
        </Button>
      )}
      {selectedOptions.length > 0 && (
        <ul style={{ marginTop: 8, paddingLeft: 0 }}>
          {selectedOptions.map((item) => (
            <li
              key={item.value}
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: 4,
                listStyle: 'none',
                border: '1px solid #ddd',
                borderRadius: 4,
                padding: '4px 8px',
                background: '#fafafa',
              }}
            >
              <span
                style={{
                  flex: 1,
                  minWidth: 0, // permite que o flex item encolha corretamente
                  overflowWrap: 'anywhere', // quebra URLs ou palavras muito longas
                  wordBreak: 'break-word', // compatibilidade extra
                }}
                title={item.label}
              >
                {item.label}
              </span>
              <button
                type="button"
                onClick={() => handleRemove(item.value)}
                style={{
                  marginLeft: 8,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                }}
                aria-label={intl.formatMessage(messages.remove)}
              >
                <Icon name={clearSVG} size="18px" color="#e40166" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </FormFieldWrapper>
  );
};

ArrayListWidget.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  required: PropTypes.bool,
  error: PropTypes.arrayOf(PropTypes.string),
  choices: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  ),
  value: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  ),
  placeholder: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  intl: PropTypes.object.isRequired,
  creatable: PropTypes.bool,
  placeholderInput: PropTypes.string,
  disabled: PropTypes.bool,
  isDisabled: PropTypes.bool,
  ref: PropTypes.any,
  onBlur: PropTypes.func,
  fieldSet: PropTypes.string,
  minLength: PropTypes.number,
  maxLength: PropTypes.number,
};

ArrayListWidget.defaultProps = {
  description: null,
  required: false,
  error: [],
  choices: [],
  value: null,
  creatable: false,
  disabled: false,
  isDisabled: false,
  ref: null,
  onBlur: null,
};

export default compose(
  injectIntl,
  injectLazyLibs(['reactSelect', 'reactSelectCreateable', 'reactSortableHOC']),
  connect(
    (state, props) => {
      const vocabBaseUrl =
        getVocabFromHint(props) ||
        getVocabFromField(props) ||
        getVocabFromItems(props);

      const vocabState =
        state.vocabularies?.[vocabBaseUrl]?.subrequests?.[state.intl.locale];

      // If the schema already has the choices in it, then do not try to get the vocab,
      // even if there is one
      if (props.items?.choices) {
        return {
          choices: props.items.choices,
          lang: state.intl.locale,
        };
      } else if (vocabState) {
        return {
          choices: vocabState.items,
          vocabBaseUrl,
          vocabLoading: vocabState.loading,
          vocabLoaded: vocabState.loaded,
          lang: state.intl.locale,
        };
      }
      return { vocabBaseUrl, lang: state.intl.locale };
    },
    { getVocabulary },
  ),
)(ArrayListWidget);
